/**
 * Author: Andrey Morozov
 * Confidential. Proprietary and confidential. Unauthorized copying or distribution is prohibited.
 */

import { ToolCallTimeline } from "@iam/design-system/agent";
import { ApprovalPrompt } from "@iam/design-system/agent";
import { StatusBadge, type StatusKind } from "@iam/design-system/chat";
import { useTranslation } from "@iam/design-system/i18n";
import { Button } from "@iam/design-system/primitives";
import { Square } from "lucide-react";
import { useMemo } from "react";
import {
  computeMetrics,
  mapApprovals,
  mapTimelineThoughts,
  mapToolCalls,
} from "../lib/eventMapping";
import type { ApprovalDecision, SessionView as Session, SessionStatus } from "../lib/types";
import { MetricsStrip } from "./MetricsStrip";

export type SessionViewProps = {
  session: Session;
  budgetUsd: number;
  onApprove: (approvalId: string, comment?: string) => void;
  onDeny: (approvalId: string, reason: string) => void;
  /** Cancel the session — shown only while it is in a non-terminal state. */
  onCancel?: () => void;
  "data-testid"?: string;
};

/** Non-terminal statuses where a cancel action is meaningful. */
const CANCELLABLE: ReadonlySet<SessionStatus> = new Set<SessionStatus>([
  "created",
  "running",
  "awaiting_approval",
  "cancelling",
]);

/** Maps a gateway session status to a DS StatusBadge kind. */
const STATUS_KIND: Record<SessionStatus, StatusKind> = {
  created: "idle",
  running: "running",
  awaiting_approval: "warning",
  cancelling: "warning",
  cancelled: "idle",
  finished: "success",
  failed: "danger",
};

/**
 * SessionView renders a single agent session: its status, reasoning, the
 * tool-call timeline, the run metrics, and any pending approval. It is a pure
 * projection of one SessionView wire object — every piece is derived from the
 * session's audit trail by the tested eventMapping helpers.
 */
export function SessionView({
  session,
  budgetUsd,
  onApprove,
  onDeny,
  onCancel,
  "data-testid": testId,
}: SessionViewProps) {
  const t = useTranslation();
  const calls = useMemo(() => mapToolCalls(session.events), [session.events]);
  const metrics = useMemo(() => computeMetrics(session.events), [session.events]);
  const thoughts = useMemo(() => mapTimelineThoughts(session.events), [session.events]);
  const approvals = useMemo(() => mapApprovals(session), [session]);

  return (
    <div className="console-session" data-testid={testId}>
      <header className="console-session__header">
        <div>
          <span className="console-session__goal-label">{t("session.goal")}</span>
          <h2 className="console-session__goal">{session.goal}</h2>
        </div>
        <div className="console-session__header-actions">
          <span data-testid="console-session-status">
            <StatusBadge kind={STATUS_KIND[session.status]} label={session.status} />
          </span>
          {onCancel && CANCELLABLE.has(session.status) && (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              iconLeft={<Square />}
              onClick={onCancel}
              data-testid="console-session-cancel"
            >
              {t("session.cancel")}
            </Button>
          )}
        </div>
      </header>

      <MetricsStrip
        metrics={metrics}
        iterations={session.iterations}
        budgetUsd={budgetUsd}
        data-testid="console-metrics"
      />

      {approvals.map((req) => (
        <ApprovalPrompt
          key={req.id}
          request={req}
          mode="inline"
          data-testid={`console-approval-${req.id}`}
          onApprove={({ comment }) => onApprove(req.id, comment)}
          onDeny={({ reason }) => onDeny(req.id, reason)}
        />
      ))}

      {thoughts.length > 0 && (
        <section className="console-thoughts" aria-label={t("thoughts.title")}>
          <h3 className="console-section__title">{t("thoughts.title")}</h3>
          {thoughts.map((th) => (
            <p key={th.id} className={`console-thought console-thought--${th.kind}`}>
              {th.text}
            </p>
          ))}
        </section>
      )}

      <section className="console-timeline" aria-label={t("timeline.title")}>
        <h3 className="console-section__title">{t("timeline.title")}</h3>
        <ToolCallTimeline
          calls={calls}
          data-testid="console-timeline"
          emptyState={<p className="console-empty">{t("timeline.empty")}</p>}
        />
      </section>

      {session.result_summary && (
        <section className="console-result" aria-label={t("session.result")}>
          <h3 className="console-section__title">{t("session.result")}</h3>
          <p className="console-result__text">{session.result_summary}</p>
        </section>
      )}
    </div>
  );
}

export type { ApprovalDecision };
